import {FC, useEffect, useState} from "react";
import axios from "axios";
import {useFeature} from "@/contexts/FeatureProvider";
import config from "@/config";
import {Button} from "@/components/ui/Button/index";
import Input from "@/components/ui/Input/index";
import {FormContainer, FormItem} from '@/components/ui/Form/index';
import {Field, Form, Formik} from 'formik'
import * as Yup from "yup";

interface WaitlistProps {
}

type WaitListFormSchema = {
  email: string
}

const Waitlist: FC<WaitlistProps> = () => {
  const {setShowFeature, isShow} = useFeature();
  
  const [isAdded, setIsAdded] = useState(false); // New state variable
  const [alreadyAdded, setAlreadyAdded] = useState(''); // New state variable
  
  const validationSchema = Yup.object().shape({
    email: Yup.string().email('Invalid email').required('Please enter your email'),
  })
  
  const onSubmit = async (
    values: WaitListFormSchema,
    setSubmitting: (isSubmitting: boolean) => void
  ) => {
    setSubmitting(true);
    setAlreadyAdded(''); // Clear previous errors
    
    try {
      const res = await axios.post(`${config.NEXT_PUBLIC_BASE_URL}/api/waitlist`, values, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      });
      
      if (res.status === 201) {
        setSubmitting(false);
        setIsAdded(true);
      }
    } catch (error: any) {
      setSubmitting(false);
      
      // Handle specific error cases
      if (error?.response?.status === 409) {
        setIsAdded(true);
        setAlreadyAdded(error.response.data.message || 'You are already on the waitlist.');
      } else if (error?.response?.status === 400) {
        setAlreadyAdded('Please fill all the required fields.');
      } else {
        // Display detailed error message from server or fallback
        const errorMessage = error?.response?.data?.message || 'Sorry something went wrong. Please try again later.';
        setAlreadyAdded(errorMessage);
      }
    }
  }
  
  useEffect(() => {
    setIsAdded(false);
    setAlreadyAdded('');
  }, [isShow]);
  
  return (
    <>
      {isShow && (
        <div
          className="fixed h-screen w-screen top-0 right-0 left-0 bottom-0 bg-black flex justify-center items-center bg-opacity-60"
          onClick={(_e) => setShowFeature(false)}
        >
          <div
            className="w-[400px] rounded-lg bg-[#f5f4ea] p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-xl font-bold text-black mb-5">Join Waitlist</h3>
            {isAdded ? (
              <>
                {alreadyAdded ? (
                  <p className="text-red-600">{alreadyAdded}</p>
                ) : (
                  <p>
                    You're now on our waitlist. We'll reach out to you as soon
                    as we can.
                  </p>
                )}
              </>
            ) : (
              <div>
                <Formik
                  initialValues={{email: ''}}
                  validationSchema={validationSchema}
                  onSubmit={(values, {setSubmitting}) => {
                    onSubmit(values, setSubmitting)
                  }}
                >
                  {({touched, errors, isSubmitting}) => (
                    <Form>
                      <FormContainer>
                        <FormItem
                          labelClass="text-xl text-[#6F6AF8] font-medium"
                          invalid={
                            (errors.email && touched.email) as boolean
                          }
                          htmlFor='mail'
                          errorMessage={errors.email}
                        >
                          <Field
                            type="text"
                            autoComplete="off"
                            name="email"
                            placeholder="Enter your email address"
                            className="border-[.5px] border-black rounded-xl px-2 py-2 mb-1 w-full"
                            component={Input}
                          />
                        </FormItem>
                        
                        <div className="mb-2 text-right">
                          <Button
                            loading={isSubmitting}
                            block
                            variant='solid'
                            shape='circle'
                            className="bg-[#1450C4] hover:bg-[#1450C4] rounded-full text-lg font-bold">
                            {isSubmitting ? 'Sending...' : 'join waitlist'}
                          </Button>
                        </div>
                      </FormContainer>
                    </Form>
                  )}
                </Formik>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Waitlist;
